import React, { useState, useEffect } from 'react';
import { Box, Flex, Text, HStack, VStack, Spinner, Icon } from '@chakra-ui/react';
import { FiArrowRight, FiClock, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import Card from 'components/Card/Card.js';

const PAGE_SIZE = 4;

const CATEGORY_LABELS = {
  clase: 'Clase',
  reunion: 'Reunión',
  evaluacion: 'Evaluación',
  actividad: 'Actividad',
  importante: 'Importante',
  cita: 'Cita',
  otro: 'Evento'
};

const CATEGORY_COLORS = {
  clase: 'sky',
  reunion: 'lav',
  evaluacion: 'butter',
  actividad: 'mint',
  importante: 'rose',
  cita: 'coral',
  otro: 'teal'
};

const dayLabelFor = (start) => {
  if (!start) return '';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const day = new Date(start); day.setHours(0, 0, 0, 0);

  if (day.getTime() === today.getTime()) return 'Hoy';
  if (day.getTime() === tomorrow.getTime()) return 'Mañana';
  return day.toLocaleDateString('es-CL', { weekday: 'short', day: 'numeric', month: 'short' }).replace(/^\w/, (c) => c.toUpperCase());
};

const TimelineRow = ({ event, isLast }) => {
  const history = useHistory();
  const start = event.start_date ? new Date(event.start_date) : null;
  const isAllDay = event.all_day == 1 || event.all_day === true;

  const hour = start && !isAllDay
    ? start.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false })
    : '--:--';
  const meridian = start && !isAllDay
    ? start.toLocaleTimeString('es-CL', { hour: '2-digit', hour12: true }).split(' ')[1] || ''
    : 'Todo el día';
  const dayLabel = dayLabelFor(start);

  const palette = CATEGORY_COLORS[event.category] || 'teal';
  const railColor = `${palette}.ink`;

  const isClickableCita = event.category === 'cita' && event._appointment;
  const handleClick = () => {
    if (!isClickableCita) return;
    const apt = event._appointment;
    history.push(`/admin/agenda?cita=${apt.id}&fecha=${apt.appointment_date}`);
  };

  return (
    <Flex
      gap="14px"
      p="13px 22px"
      borderTop="1px solid"
      borderColor="cream.line"
      align="flex-start"
      cursor={isClickableCita ? 'pointer' : 'default'}
      _hover={isClickableCita ? { bg: 'coral.tint' } : undefined}
      onClick={handleClick}
    >
      <Box flex="0 0 58px" textAlign="right" pt="3px">
        <Text fontSize="10.5px" color="coral.700" fontWeight="800" textTransform="uppercase" letterSpacing="0.02em" mb="1px">
          {dayLabel}
        </Text>
        <Text fontWeight="800" fontSize="15px" lineHeight="1">{hour}</Text>
        <Text fontSize="11px" color="ink.400" fontWeight="700" textTransform="uppercase">{meridian}</Text>
      </Box>
      <Flex flex="0 0 auto" direction="column" align="center" pt="2px" color={railColor}>
        <Box w="13px" h="13px" borderRadius="50%" border="3px solid white" boxShadow="0 0 0 2.5px currentColor" flexShrink={0} />
        {!isLast && <Box w="2px" flex="1" bg="cream.line" mt="5px" minH="16px" />}
      </Flex>
      <Box flex="1" bg="cream.bg" border="1px solid" borderColor="cream.line" borderRadius="14px" p="13px 16px">
        <Text fontWeight="700" fontSize="14.5px" lineHeight="1.2" noOfLines={1}>
          {event.title || 'Evento sin título'}
        </Text>
        {event.description && (
          <Text fontSize="12.5px" color="ink.700" fontWeight="500" mt="3px" noOfLines={2}>
            {event.description}
          </Text>
        )}
        <HStack mt="10px" spacing="8px" flexWrap="wrap">
          <Box
            fontSize="12px"
            fontWeight="700"
            px="12px"
            py="4px"
            borderRadius="999px"
            bg={`${palette}.bg`}
            color={`${palette}.ink`}
          >
            {CATEGORY_LABELS[event.category] || 'Evento'}
          </Box>
          {event.location && (
            <Text fontSize="12px" color="ink.400" fontWeight="600">{event.location}</Text>
          )}
        </HStack>
      </Box>
    </Flex>
  );
};

const AgendaTodayCard = ({ events = [], isLoading = false }) => {
  const history = useHistory();
  const [page, setPage] = useState(0);

  const totalPages = Math.max(1, Math.ceil(events.length / PAGE_SIZE));

  // Si la lista de eventos cambia (ej. recarga periódica) y la página actual queda fuera de rango, volver al inicio
  useEffect(() => {
    setPage((p) => Math.min(p, totalPages - 1));
  }, [totalPages]);

  const pageEvents = events.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  return (
    <Card p={0} borderRadius="22px" overflow="hidden">
      <Flex align="center" gap="10px" p="18px 22px 6px">
        <Text fontSize="16px" fontWeight="800" letterSpacing="-0.01em" whiteSpace="nowrap">
          Agenda de hoy
        </Text>
        <Box fontSize="12px" fontWeight="700" color="ink.400" bg="cream.bg2" px="9px" py="3px" borderRadius="999px" whiteSpace="nowrap">
          {events.length} {events.length === 1 ? 'evento' : 'eventos'}
        </Box>
        <Box
          as="span"
          ml="auto"
          fontSize="13px"
          fontWeight="700"
          color="coral.700"
          whiteSpace="nowrap"
          cursor="pointer"
          onClick={() => history.push('/admin/agenda')}
          display="flex"
          alignItems="center"
          gap="4px"
        >
          Ver agenda <Icon as={FiArrowRight} />
        </Box>
      </Flex>

      {isLoading ? (
        <Flex justify="center" py="24px">
          <Spinner color="coral.500" />
        </Flex>
      ) : events.length === 0 ? (
        <VStack py="24px" spacing={2} color="ink.400">
          <Icon as={FiClock} boxSize="24px" />
          <Text fontSize="sm">No hay eventos programados para hoy ni próximos días</Text>
        </VStack>
      ) : (
        <>
          {pageEvents.map((event, index) => (
            <TimelineRow key={event.id || index} event={event} isLast={index === pageEvents.length - 1} />
          ))}

          {totalPages > 1 && (
            <Flex align="center" justify="space-between" p="10px 22px" borderTop="1px solid" borderColor="cream.line">
              <Box
                as="button"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                opacity={page === 0 ? 0.35 : 1}
                cursor={page === 0 ? 'default' : 'pointer'}
                disabled={page === 0}
                display="flex"
                alignItems="center"
                gap="4px"
                fontSize="12.5px"
                fontWeight="700"
                color="ink.700"
              >
                <Icon as={FiChevronLeft} /> Anterior
              </Box>
              <Text fontSize="12px" fontWeight="700" color="ink.400">
                Página {page + 1} de {totalPages}
              </Text>
              <Box
                as="button"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                opacity={page === totalPages - 1 ? 0.35 : 1}
                cursor={page === totalPages - 1 ? 'default' : 'pointer'}
                disabled={page === totalPages - 1}
                display="flex"
                alignItems="center"
                gap="4px"
                fontSize="12.5px"
                fontWeight="700"
                color="ink.700"
              >
                Siguiente <Icon as={FiChevronRight} />
              </Box>
            </Flex>
          )}
        </>
      )}
    </Card>
  );
};

export default AgendaTodayCard;
